<template>
  <VanDialog :show="show" @update:show="emit('update:show', $event)">
    <template #title>
      <div class="text-center text-base font-bold">{{ title }}</div>
    </template>

    <div :class="['px-6', !checked ? 'opacity-50' : '']">
      <div v-if="showLessInfinity" class="text-center">
        <van-checkbox
          :model-value="checked"
          @update:model-value="clickLessInfinity"
          shape="square"
        >
          -∞
        </van-checkbox>
      </div>
      <div v-else-if="showMoreInfinity" class="text-center">
        <van-checkbox
          :model-value="checked"
          @update:model-value="clickMoreInfinity"
          shape="square"
        >
          +∞
        </van-checkbox>
      </div>
    </div>
    <div
      :class="[
        checked ? 'opacity-50  cursor-not-allowed pointer-events-none' : '',
      ]"
    >
      <div v-if="withOperator" class="px-6 py-2">
        <div class="flex gap-4 mb-2">
          <span>{{ t("game.values.operation") }}</span>
        </div>
        <div class="flex gap-2 flex-wrap mb-4">
          <button
            v-for="item in conditionList"
            :class="[
              'btn  btn-xs',
              operator === item.value ? 'btn-primary ' : 'btn-secondary ',
            ]"
            @click="operator = item.value"
          >
            {{ item.value }} ({{ item.label }})
          </button>
        </div>
        <div>
          <span>{{ t("game.score") }}</span>
        </div>
      </div>
      <VanCellGroup inset>
        <VanField
          v-model.number="score"
          class="text-center my-2"
          type="number"
        />
      </VanCellGroup>
      <div class="flex gap-4 py-4 px-6">
        <span>{{ min }}</span>
        <VanSlider v-model="score" :max="max" :min="min">
          <template #button>
            <div
              class="w-[26px] text-white text-xs leading-[18px] text-center bg-primary rounded-[100px]"
            >
              {{ score && score >= 0 && min < 0 ? `+${score}` : score }}
            </div>
          </template>
        </VanSlider>
        <span>{{ max >= 0 && min < 0 ? `+${max}` : max }}</span>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-center py-4">
        <button class="btn btn-primary btn-sm mr-2" @click="clickConfirm">
          {{ t("Buttons.confirm") }}
        </button>

        <button class="btn btn-sm" @click="emit('update:show', false)">
          {{ t(`story.g.cancel`) }}
        </button>
      </div>
    </template>
  </VanDialog>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import i18n from "@/locales";
import { watch } from "vue";

const { t } = useI18n();
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: i18n.global.t("ConfirmModal.rating"),
  },
  score: {
    type: Number,
    default: 0,
  },
  min: {
    type: Number,
    default: -100,
  },
  max: {
    type: Number,
    default: 100,
  },
  operator: {
    type: String,
    default: "",
  },
  withOperator: {
    type: Boolean,
    default: false,
  },
  showLessInfinity: {
    type: Boolean,
    default: false,
  },
  showMoreInfinity: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:show", "confirm"]);
const checked = ref(false);

const conditionList = [
  {
    label: t(`game.values.greaterEqual`),
    value: ">=",
  },
  {
    label: t(`game.values.lessEqual`),
    value: "<=",
  },
  {
    label: t(`game.values.equal`),
    value: "==",
  },
  {
    label: t(`game.values.greater`),
    value: ">",
  },
  {
    label: t(`game.values.less`),
    value: "<",
  },
];

const score = ref<number | null>(0);
const operator = ref(props.operator);

watch(
  () => props.show,
  (val) => {
    if (val) {
      score.value = props.score;
      operator.value = props.operator;
      if (
        (props.showLessInfinity || props.showMoreInfinity) &&
        score.value === null
      ) {
        checked.value = true;
      } else {
        checked.value = false;
      }
    }
  }
);

watch(score, (val) => {
  if (val! <= -100) {
    score.value = -100;
  } else if (val! >= 100) {
    score.value = 100;
  }
});

const clickConfirm = async () => {
  emit(
    "confirm",
    typeof score.value === "number" ? score.value : null,
    operator.value
  );
  emit("update:show", false);
};

let tmpScore = 0;
const clickLessInfinity = () => {
  checked.value = !checked.value;
  if (checked.value) {
    tmpScore = score.value!;
    score.value = null;
  } else {
    score.value = tmpScore;
  }
};

const clickMoreInfinity = () => {
  checked.value = !checked.value;
  if (checked.value) {
    tmpScore = score.value!;
    score.value = null;
  } else {
    score.value = tmpScore;
  }
};
</script>
